import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  Save,
  Send,
  X,
  Plus,
  Trash2,
  FileText,
  User,
  Ship,
  Package,
  DollarSign,
  MapPin,
  Calendar,
  Info,
  Copy,
  ChevronDown,
  ChevronUp,
  Building2,
  Truck,
  AlertTriangle,
  Globe,
  ArrowRight,
  Check,
  Printer,
  Hash,
  Anchor,
  CreditCard,
  NotepadText,
  Mail,
  Sparkles,
  Loader2,
  Pencil,
  BookOpen,
  Users,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import axios from "axios";
import moment from "moment";

import ValidationRateQuote from "@/validation/RateQuote.json";
import { getAddressBookNotes } from "@/redux/RateQuote/actions";
import {
  getExpBookingRequestById,
  createExportBookingRequest,
  updateExportBookingRequest,
} from "@/redux/BookingRequest/actions";
import { SearchBar } from "@/components/SearchBox/SearchBar";
import { getAddressAPI, getContactDetails } from "@/redux/Purchase/actions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { validateForm } from "@/components/ui/useFormValidation";
import { callLoader } from "@/redux/Loader/actions";
import { getClauselist } from "@/redux/Common/actions";
import CelebrationButton from "@/components/ui/CelebrationButton/CelebrationButton";
import { showToast } from "@/redux/Toast/actions";
import CarrierSummeryModal from "@/components/Modal/CarrierSummeryModal";
import CreateBookingModal from "@/components/Modal/CreateBookingModal";
import MultiCarrierSection from "./MultiCarrierSection";
import { GetVesselSchedule } from "@/redux/Booking/actions";
import PageHeader from "@/components/FormHeader/formPageHeader";
import { InlineCountryFlag } from "@/components/shipment/CountryBadge";
import { useAlertModal } from "@/hooks/useAlertModal";
import AlertModal from "@/components/Modal/AlertModal";

const InputField = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const NewBookingRequest = () => {
  const alert = useAlertModal();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    countryList,
    documentTypePurchaseList,
    documentTypeSalesList,
    incoTermsList,
    quantityTypeList,
    salesPersonList,
    uomTypeList,
    commodityGroupList,
    containerTypeList,
    operatorList,
    moveTypeList,
    fileManagerList,
    currency_list,
    charges_basis_list,
  } = useSelector((state) => state.common);
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const [MasterCompanyDetails] = JSON.parse(
    localStorage.getItem("MasterCompanyDetails") ?? "[{}]",
  );
  const { isAnimate } = useSelector((state) => state.toast);
  const unitToCm = { CM: 1, INCH: 2.54, FEET: 30.48, MT: 100 };

  const { id: idQuote } = useParams();
  const [formData, setFormData] = useState();
  const [carriers, setCarriers] = useState([]);
  const [cargoUnits, setCargoUnits] = useState([]);
  const [vessel, setVessel] = useState([]);
  const [container, setContainer] = useState([]);
  const [showRateQuoteNote, setShowRateQuoteNote] = useState(false);
  const [rateQuoteNote, setRateQuoteNote] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [originalFormClauses, setOriginalFormClauses] = useState([]);
  const [showCarrierSummery, setShowCarrierSummery] = useState(false);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  // Multi-carrier state
  const [quoteType, setQuoteType] = useState("single");
  const [multiCarriers, setMultiCarriers] = useState([]);
  // AI Tips state
  const [showAITip, setShowAITip] = useState(false);
  const [aiTipLoading, setAiTipLoading] = useState(false);
  const [aiTipContent, setAiTipContent] = useState("");
  // Vessel Schedule state
  const [showModal, setShowModal] = useState(false);
  const [vesselList, setVesselList] = useState([]);
  const [selectedVessels, setSelectedVessels] = useState([]);
  const [error, setError] = useState("");

  // Prevent duplicate row on re-render
  const isInitialized = useRef(false);

  // Section refs for scroll navigation
  const customerRef = useRef(null);
  const carrierRef = useRef(null);
  const containerRef = useRef(null);
  const routingRef = useRef(null);
  const chargesRef = useRef(null);
  const remarksRef = useRef(null);
  const commonClauseRef = useRef(null);
  const sectionRefs = {
    customer: customerRef,
    carrier: carrierRef,
    container: containerRef,
    routing: routingRef,
    charges: chargesRef,
    remarks: remarksRef,
    commonClause: commonClauseRef,
  };

  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    carrier: true,
    container: true,
    routing: true,
    charges: true,
    remarks: true,
    clauses: true,
    vessel: true,
    cargo: true,
    commonClause: true,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    "Customer Info",
    "Carrier Details",
    "Container & Commodity",
    "Routing",
    "Charges",
    "Remarks & Notes",
    "Clause",
  ];

  const inputClass =
    "w-full px-3 py-[0.6rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all";
  const selectClass =
    "w-full px-3 py-[0.5rem] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer";

  const formatDate = (date) =>
    date ? new Date(date).toISOString().split("T")[0] : "";

  const stepSectionMap = useMemo(
    () => [
      "customer",
      "carrier",
      "container",
      "routing",
      "charges",
      "remarks",
      "commonClause",
    ],
    [],
  );

  const SECTION_COLORS = {
    blue: "bg-blue-500",
    teal: "bg-teal-500",
    violet: "bg-violet-500",
    cyan: "bg-cyan-500",
    green: "bg-emerald-500",
    indigo: "bg-indigo-500",
    amber: "bg-amber-500",
    orange: "bg-orange-500",
    rose: "bg-rose-500",
    dark: "bg-slate-700",
  };

  // IntersectionObserver for progress bar
  useEffect(() => {
    const observers = [];
    stepSectionMap.forEach((key, index) => {
      const ref = sectionRefs[key];
      if (!ref?.current) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setCurrentStep(index);
        },
        { root: null, rootMargin: "-30% 0px -60% 0px", threshold: 0 },
      );
      observer.observe(ref.current);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [formData, stepSectionMap]);

  const scrollToSection = (index) => {
    const sectionKey = stepSectionMap[index];
    const ref = sectionRefs[sectionKey];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setExpandedSections((prev) => ({ ...prev, [sectionKey]: true }));
    }
  };

  const isSectionFilled = useCallback(
    (key) => {
      if (!formData) return false;
      switch (key) {
        case "customer":
          return !!(formData?.customerName || formData?.customerCode);
        case "carrier":
          return !!(formData?.carrierName || formData?.carrierSCAC);
        case "container":
          return !!(
            container?.length > 0 ||
            formData?.moveType ||
            formData?.shippingType
          );
        case "routing":
          return !!(
            formData?.portOfLoad ||
            formData?.portOfDischarge ||
            formData?.portOfLoadCode ||
            formData?.portOfDischargeCode
          );
        case "charges":
          return !!(carriers?.length > 0);
        case "remarks":
          return !!(
            formData?.customerRemarks ||
            formData?.internalRemarks ||
            formData?.countryClause
          );
        case "commonClause":
          return !!clauses?.some((clause) => clause.applied);
        default:
          return false;
      }
    },
    [formData, container, carriers, clauses],
  );

  const getAITip = async () => {
    setAiTipLoading(true);
    setShowAITip(true);
    setAiTipContent("");
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
      const summary = `Booking Request form data: Customer=${formData?.customerName || "Not set"}, Carrier=${formData?.carrierName || "Not set"}, Origin=${formData?.portOfLoad || "Not set"}, Destination=${formData?.portOfDischarge || "Not set"}, ShippingType=${formData?.shippingTerm || "FCL"}, EffectiveDate=${formData?.effectiveDate || "Not set"}, ExpiryDate=${formData?.valideDate || "Not set"}, MoveType=${formData?.moveType || "Not set"}, Containers=${container?.length || 0} items, Charges=${carriers?.length || 0} items`;
      const response = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
        message: `You are an AI assistant for a shipping logistics platform. Analyze this booking request data and provide 3-4 brief, actionable tips to improve the booking or flag potential issues. Keep tips concise (1-2 lines each). Data: ${summary}`,
        context: "booking_request_tips",
      });
      setAiTipContent(
        response.data?.response ||
          response.data?.message ||
          "Unable to generate tips at this time.",
      );
    } catch (err) {
      setAiTipContent(
        "AI tips temporarily unavailable. Please check that all required fields are filled.",
      );
    }
    setAiTipLoading(false);
  };

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    if (idQuote != 0 && idQuote != null && idQuote != undefined) {
      _getFormData();
    } else {
      _setNewFormData();
    }
  }, [idQuote]);

  useEffect(() => {
    convertToCM();
  }, [cargoUnits]);

 const _getFormData = async () => {
  const resultData = await dispatch(
    getExpBookingRequestById(idQuote, MasterCompanyDetails?.CompanyId)
  );
  if (!resultData) return;

  _getAddressBookNotes(idQuote);

  const carrierDetails = Array.isArray(resultData?.bookingRequestCarrierDetails)
    ? resultData.bookingRequestCarrierDetails
    : [];
  const first = carrierDetails[0] || {};

  setQuoteType(resultData?.IsMultiCarrier ? "multi" : "single");
  setCarriers(first?.rates || []);
  setVessel(first?.vessels || []);

  if ((first?.containers?.length || 0) > 0) {
    const allContainers = first.containers;
    const activeAll = allContainers.filter((c) => c?.isDeleted !== 1);
    const deleted   = allContainers.filter((c) => c?.isDeleted === 1);
    setContainer([...deleted, ...activeAll]);
  }

  resultData.carrierSno        = first?.carrierSno        || 0;
  resultData.carrierName       = first?.carrierName       || "";
  resultData.carrierSCAC       = first?.carrierSCAC       || "";
  resultData.carrierContract   = first?.carrierContract   || "";
  resultData.carrierHide       = first?.carrierHide       || false;
  resultData.carrierDetailId   = first?.carrierDetailId   || 0;
  resultData.set1TotalSell     = first?.set1TotalSell     || 0.0;
  resultData.set1TotalBuy      = first?.set1TotalBuy      || 0.0;
  resultData.set2TotalSell     = first?.set2TotalSell     || 0.0;
  resultData.set2TotalBuy      = first?.set2TotalBuy      || 0.0;
  resultData.set3TotalSell     = first?.set3TotalSell     || 0.0;
  resultData.set3TotalBuy      = first?.set3TotalBuy      || 0.0;
  resultData.set1Profit        = first?.set1Profit        || 0.0;
  resultData.set2Profit        = first?.set2Profit        || 0.0;
  resultData.set3Profit        = first?.set3Profit        || 0.0;
  resultData.set1ProfitMargin  = first?.set1ProfitMargin  || 0.0;
  resultData.set2ProfitMargin  = first?.set2ProfitMargin  || 0.0;
  resultData.set3ProfitMargin  = first?.set3ProfitMargin  || 0.0;

  setFormData(resultData);
  setCargoUnits(resultData?.bookingRequestUnits || []);
  getClauselistData(resultData?.bookingRequestClauses || []);
  setOriginalFormClauses(resultData?.bookingRequestClauses || []);
};

  const _setNewFormData = async () => {
    setFormData({
      shippingType: "FCL",
      quotedBy: authUser?.firstName + " " + authUser?.lastName,
      effectiveDate: moment().format("YYYY-MM-DD"),
      valideDate: moment().add(30, "days").format("YYYY-MM-DD"),
      moveType: "PORT TO PORT",
      moveType_TSC: "PORT TO PORT",
      officeType: "NEW YORK",
    });
    addContainerChange();
    getClauselistData([]);
    setOriginalFormClauses([]);
  };

  const _getAddressBookNotes = async (id) => {
    getAddressBookNotes(id).then((res) => {
      setRateQuoteNote(res || []);
    });
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ─── SectionHeader: identical to NewRateQuote ───────────────────────────
  const SectionHeader = ({
    icon: Icon,
    title,
    color = "blue",
    section,
    badge,
    action,
  }) => {
    const iconBg = SECTION_COLORS[color] ?? color ?? SECTION_COLORS.blue;
    const isDark = color === "dark" || color === "bg-slate-700";
    return (
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between p-4 border-b bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 mx-2 rounded-lg shadow-md ${iconBg}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <h2
            className={`text-sm font-semibold ${isDark ? "text-slate-100" : "text-slate-900 dark:text-slate-200"}`}
          >
            {title}
          </h2>
          {badge && (
            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {action}
          <div
            className={`p-1.5 rounded-md transition-colors ${expandedSections[section] ? "bg-indigo-100 dark:bg-indigo-900/30" : "bg-slate-200 dark:bg-slate-700"}`}
          >
            {expandedSections[section] ? (
              <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            )}
          </div>
        </div>
      </button>
    );
  };

  // ─── Rate charges ────────────────────────────────────────────────────────
  const addChargeRow = () => {
    setCarriers((prev) => [
      ...prev,
      {
        rateid: 0,
        carriersno: formData?.carrierSno || 0,
        freightid: 0,
        chargedescription: "",
        all_in: false,
        currency: "USD",
        roe: 1,
        set1sellrate: 0.0,
        set1buyrate: 0.0,
        set2sellrate: 0.0,
        set2buyrate: 0.0,
        set3sellrate: 0.0,
        set3buyrate: 0.0,
        desccode: "",
        basis: "",
        isdeleted: false,
        iscustadditionalrate: 0,
        vendorid: 0,
        vendor_name: "",
        paymentmode: "",
      },
    ]);
  };

  const handleRateChange = (index, field, value) => {
    const updated = [...carriers];
    updated[index][field] = value;
    calculateTotals(updated);
    setCarriers(updated);
  };

  const _getContainerName = (index) => {
    const notDeleted = container?.filter((r) => r?.isDeleted == 0);
    return notDeleted?.[index]?.contTypeName;
  };

  const calculateTotals = (data = []) => {
    const totals = data.reduce(
      (acc, item) => {
        acc.set1TotalSell += parseFloat(item.set1sellrate) || 0;
        acc.set1TotalBuy += parseFloat(item.set1buyrate) || 0;
        acc.set2TotalSell += parseFloat(item.set2sellrate) || 0;
        acc.set2TotalBuy += parseFloat(item.set2buyrate) || 0;
        acc.set3TotalSell += parseFloat(item.set3sellrate) || 0;
        acc.set3TotalBuy += parseFloat(item.set3buyrate) || 0;
        return acc;
      },
      {
        set1TotalSell: 0,
        set1TotalBuy: 0,
        set2TotalSell: 0,
        set2TotalBuy: 0,
        set3TotalSell: 0,
        set3TotalBuy: 0,
      },
    );
    handleInputChange({
      target: { name: "set1TotalSell", value: totals.set1TotalSell },
    });
    handleInputChange({
      target: { name: "set1TotalBuy", value: totals.set1TotalBuy },
    });
    handleInputChange({
      target: { name: "set2TotalSell", value: totals.set2TotalSell },
    });
    handleInputChange({
      target: { name: "set2TotalBuy", value: totals.set2TotalBuy },
    });
    handleInputChange({
      target: { name: "set3TotalSell", value: totals.set3TotalSell },
    });
    handleInputChange({
      target: { name: "set3TotalBuy", value: totals.set3TotalBuy },
    });
    handleInputChange({
      target: {
        name: "set1Profit",
        value: totals.set1TotalSell - totals.set1TotalBuy,
      },
    });
    handleInputChange({
      target: {
        name: "set2Profit",
        value: totals.set2TotalSell - totals.set2TotalBuy,
      },
    });
    handleInputChange({
      target: {
        name: "set3Profit",
        value: totals.set3TotalSell - totals.set3TotalBuy,
      },
    });
    handleInputChange({
      target: {
        name: "set1ProfitMargin",
        value:
          totals.set1TotalSell > 0 && totals.set1TotalBuy >= 0
            ? ((totals.set1TotalSell - totals.set1TotalBuy) /
                totals.set1TotalBuy) *
              100
            : 0,
      },
    });
    handleInputChange({
      target: {
        name: "set2ProfitMargin",
        value:
          totals.set2TotalSell > 0 && totals.set2TotalBuy >= 0
            ? ((totals.set2TotalSell - totals.set2TotalBuy) /
                totals.set2TotalBuy) *
              100
            : 0,
      },
    });
    handleInputChange({
      target: {
        name: "set3ProfitMargin",
        value:
          totals.set3TotalSell > 0 && totals.set3TotalBuy >= 0
            ? ((totals.set3TotalSell - totals.set3TotalBuy) /
                totals.set3TotalBuy) *
              100
            : 0,
      },
    });
  };

  // ─── Cargo ───────────────────────────────────────────────────────────────
  const addCargoRow = () => {
    setCargoUnits((prev) => [
      ...prev,
      {
        id: 0,
        pices_No: 1,
        units_Type_ID: 0,
        description: "",
        dimensions_L: 0.0,
        dimensions_W: 0.0,
        dimensions_H: 0.0,
        dimensions_Type: "",
        weights_KG: 0.0,
        weights_LB: 0.0,
        measurment_CFT: 0.0,
        measurment_CBM: 0.0,
        sealNumber: "",
        container_ForUnit: "",
        hsCode: "",
      },
    ]);
  };

  const handleCargoChange = (index, field, value) => {
    const updated = [...cargoUnits];
    updated[index][field] = value;
    const convertedItem = convertToCM(updated[index]);
    updated[index] = convertedItem;
    if (field === "weights_KG")
      updated[index]["weights_LB"] = parseFloat((value * 2.20462).toFixed(2));
    if (field === "weights_LB")
      updated[index]["weights_KG"] = parseFloat((value / 2.20462).toFixed(2));
    setCargoUnits(updated);
  };

  // ─── Container ───────────────────────────────────────────────────────────
  const handleContainerChange = (index, field, value) => {
    const updated = [...container];
    updated[index][field] = value;
    updated[index]["lineSeqno"] = index + 1;
    setContainer(updated);
  };

  const addContainerChange = () => {
    const notDeletedCount = container.filter((r) => r?.isDeleted == 0).length;
    if (notDeletedCount >= 3) {
      dispatch(showToast("Max 3 container can add!", "error"));
      return;
    }
    setContainer((prev) => [
      ...prev,
      {
        containerId: 0,
        numberOfContainer: 1,
        contTypeCode: "42G0",
        contTypeName: "40 STD",
        lineSeqno: 0,
        carrierId: formData?.carrierSno || 0,
        freightId: 0,
        booked: false,
        isDeleted: 0,
      },
    ]);
  };

  const deleteContainerChange = (index, field, value, tempIndex) => {
    alert.showAlert({
      title: "Confirm Deletion",
      description:
        "Are you sure you want to delete this container? If you delete, the charges will be removed automatically related to this container.",
      icon: AlertTriangle,
      iconColor: "text-yellow-500",
      confirmText: "Yes, Delete",
      onConfirm: () => {
        const updated = [...container];
        updated[index][field] = value;
        updated[index]["lineSeqno"] = index + 1;
        const isDeleted = updated?.filter((r) => r?.isDeleted == 1) || [];
        const isActived = updated?.filter((r) => r?.isDeleted == 0) || [];
        setContainer([...isDeleted, ...isActived]);
        _clearContainerRelatedCharges(tempIndex);
      },
      cancelText: "Cancel",
    });
  };

  const _clearContainerRelatedCharges = (index) => {
    if (index === 0) {
      const allCarriers = [...carriers];
      allCarriers.forEach((carrier) => {
        carrier.isdeleted = true;
      });
      setCarriers(allCarriers);
      calculateTotals();
    } else {
      const allCarriers = [...carriers];
      allCarriers.forEach((carrier) => {
        carrier["set" + (index + 1) + "sellrate"] = 0.0;
        carrier["set" + (index + 1) + "buyrate"] = 0.0;
      });
      setCarriers(allCarriers);
      calculateTotals(allCarriers);
    }
  };

  // ─── Vessels ─────────────────────────────────────────────────────────────
  const handleVesselChange = (index, field, value) => {
    const updated = [...vessel];
    updated[index][field] = value;
    updated[index]["lineSeqno"] = index + 1;
    setVessel(updated);
  };

  const addVesselCharge = () => {
    const notDeletedCount = vessel.filter((r) => r?.isDeleted == 0).length;
    if (notDeletedCount >= 3) {
      dispatch(showToast("Max 3 Vessel Charge Can Add!", "error"));
      return;
    }
    setVessel((prev) => [
      ...prev,
      {
        id: 0,
        vesselId: 0,
        vesselName: "",
        voyage: "",
        etd: null,
        lineSeqNo: 0,
        eta: null,
        transitTime: "",
        carrierId: formData?.carrierSno || 0,
        freightId: 0,
        cargoCutoff: null,
        isDeleted: 0,
      },
    ]);
  };

  const countDays = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid()) return "";
    const days = end.diff(start, "days");
    return isNaN(days) ? "" : days.toString();
  };

  // ─── Address ─────────────────────────────────────────────────────────────
  const _setAddress = async (type, id) => {
    const address = await getAddressAPI(id);
    setFormData((prev) => ({ ...prev, [`${type}`]: address }));
  };
  const _setCotact = async (id) => {
    const contact = await getContactDetails(id);
    setFormData((prev) => ({
      ...prev,
      customerEmail: contact?.email,
      customerTel: contact?.addressphno,
      contactPerson: contact?.contactname,
      salesPersonSno: contact?.salespersonid,
    }));
  };

  // ─── Calculation ─────────────────────────────────────────────────────────
  const convertToCM = (item) => {
    if (
      item &&
      item?.dimensions_Type &&
      item?.dimensions_L &&
      item?.dimensions_W &&
      item?.dimensions_H
    ) {
      const lCm = item.dimensions_L * (unitToCm[item.dimensions_Type] || 1);
      const wCm = item.dimensions_W * (unitToCm[item.dimensions_Type] || 1);
      const hCm = item.dimensions_H * (unitToCm[item.dimensions_Type] || 1);
      const vol = lCm * wCm * hCm;
      item.measurment_CBM = parseFloat((vol / 1_000_000).toFixed(3));
      item.measurment_CFT = parseFloat((vol / 28_316.8466).toFixed(3));
      return item;
    }
    return item;
  };

  // ─── Clauses ─────────────────────────────────────────────────────────────
  const syncClausesWithFormData = (apiClauses, formClauses) => {
    if (!apiClauses || apiClauses.length === 0) return apiClauses;
    if (!formClauses || formClauses.length === 0) {
      return apiClauses.map((item) => ({
        ...item,
        applied: false,
        id: null,
        bookingId: null,
      }));
    }
    return apiClauses.map((apiClause) => {
      const existing = formClauses.find(
        (f) => f.clauseTypeDescID === apiClause.ClauseTypeDescriptionID,
      );
      return {
        ...apiClause,
        applied: existing ? existing.isActive : false,
        id: existing ? existing.id : null,
        bookingId: existing ? existing.bookingId : null,
      };
    });
  };

  const getClauselistData = async (formClauses = []) => {
    try {
      const clauseData = await dispatch(
        getClauselist({ IPageNo: 1, IPageRecords: 1000, Type: 3 }),
      );
      const transformed = clauseData.map((item) => ({
        ClauseTypeDescriptionID: item.ClauseTypeDescriptionID,
        ClauseType: item.ClauseType,
        ClauseDescription: item.ClauseDescription,
        applied: false,
        id: null,
        bookingId: null,
      }));
      setClauses(syncClausesWithFormData(transformed, formClauses));
    } catch (error) {
      console.error("Error loading clauses:", error);
    }
  };

  const prepareClausesForAPI = () => {
    const payloadMap = new Map();
    clauses?.forEach((clause) => {
      if (clause.applied === true) {
        payloadMap.set(clause.ClauseTypeDescriptionID, {
          id: clause.id || 0,
          rateQuoteID: idQuote ?? 0,
          clauseTypeDescID: clause.ClauseTypeDescriptionID,
          clauseDescription: clause.ClauseDescription,
          isActive: true,
        });
      }
    });
    originalFormClauses?.forEach((oldClause) => {
      const stillApplied = clauses?.some(
        (c) =>
          c.ClauseTypeDescriptionID === oldClause.clauseTypeDescID &&
          c.applied === true,
      );
      if (!stillApplied) {
        payloadMap.set(oldClause.clauseTypeDescID, {
          id: oldClause.id ?? 0,
          rateQuoteID: idQuote ?? 0,
          clauseTypeDescID: oldClause.clauseTypeDescID,
          clauseDescription: oldClause.clauseDescription,
          isActive: false,
        });
      }
    });
    return Array.from(payloadMap.values());
  };

  const handleClauseChange = (clauseId, field, value) => {
    if (field === "applied") {
      setClauses((prevClauses) =>
        prevClauses.map((clause) => {
          if (clause.ClauseTypeDescriptionID === clauseId) {
            const newApplied =
              typeof value === "boolean" ? value : !clause.applied;
            return {
              ...clause,
              applied: newApplied,
              id: 0,
              bookingId: newApplied
                ? clause.bookingId || formData?.id || 0
                : clause.bookingId,
            };
          }
          return clause;
        }),
      );
    }
  };

  // ─── Add carrier ─────────────────────────────────────────────────────────
  const handleAddCarrier = () => {
    const newCarrier = {
      carrierDetailId: 0,
      carrierSno: 0,
      carrierName: "",
      carrierSCAC: "",
      carrierContract: "",
      carrierHide: false,
      rateCharges: [],
      rateQuoteContainer: [],
      ratequoteVessel: [],
    };
    const updatedCarriers = formData?.bookingRequestCarrierDetails
      ? [...formData.bookingRequestCarrierDetails]
      : [];
    updatedCarriers.push(newCarrier);
    setFormData((prev) => ({ ...prev, CarrierDetails: updatedCarriers }));
  };

  // ─── Form Submit ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    try {
      dispatch(callLoader(1));
      const checkValidation = validateForm({
        data: formData,
        items: [],
        rules: ValidationRateQuote,
      });
      if (Object.keys(checkValidation).length > 0) return;
      const carriersPl = carriers.map((e) =>
        e.carriersno === 0 ? { ...e, carriersno: formData?.carrierSno } : e,
      );
      const containerPl = container.map((e) =>
        e.carrierId === 0 ? { ...e, carrierId: formData?.carrierSno } : e,
      );
      const vesselPl = vessel.map((e) =>
        e.carrierId === 0 ? { ...e, carrierId: formData?.carrierSno } : e,
      );

      var CarrierDetails = [];
      if (quoteType === "multi") {
        CarrierDetails = formData?.bookingRequestCarrierDetails;
      } else {
        if (!formData?.carrierName) {
          dispatch(showToast("Please select carrier!", "error"));
          return;
        }
        CarrierDetails = [
          {
            carrierDetailId: formData?.carrierDetailId || 0,
            carrierSno: formData?.carrierSno || 0,
            carrierName: formData?.carrierName || "",
            carrierSCAC: formData?.carrierSCAC || "",
            carrierContract: formData?.carrierContract || "",
            carrierHide: formData?.carrierHide || false,
            rateCharges: carriersPl,
            rateQuoteContainer: containerPl,
            ratequoteVessel: vesselPl,
            set1TotalSell: formData?.set1TotalSell || 0.0,
            set1TotalBuy: formData?.set1TotalBuy || 0.0,
            set2TotalSell: formData?.set2TotalSell || 0.0,
            set2TotalBuy: formData?.set2TotalBuy || 0.0,
            set3TotalSell: formData?.set3TotalSell || 0.0,
            set3TotalBuy: formData?.set3TotalBuy || 0.0,
            set1Profit: formData?.set1Profit || 0.0,
            set2Profit: formData?.set2Profit || 0.0,
            set3Profit: formData?.set3Profit || 0.0,
            set1ProfitMargin: formData?.set1ProfitMargin || 0.0,
            set2ProfitMargin: formData?.set2ProfitMargin || 0.0,
            set3ProfitMargin: formData?.set3ProfitMargin || 0.0,
          },
        ];
      }
      CarrierDetails = CarrierDetails.map((carrier) => ({
        ...carrier,
        rateCharges: carrier.rateCharges.filter(
          (charge) =>
            charge.chargedescription && charge.chargedescription.trim() !== "",
        ),
      }));

      const payload = {
        ...formData,
        IsMultiCarrier: quoteType === "multi",
        CarrierDetails,
        cargoUnits,
        rateQuoteClauses: prepareClausesForAPI(),
        companyId: authUser?.companyId,
      };

      if (idQuote != 0 && idQuote != null && idQuote != undefined) {
        await updateExportBookingRequest(payload)
          .then(() => {
            _getFormData();
            dispatch(
              showToast(
                "Successfully Updated Booking Request!",
                "success",
                true,
              ),
            );
          })
          .catch(() => {
            dispatch(showToast("Something went wrong", "error"));
          });
      } else {
        await createExportBookingRequest(payload)
          .then(() => {
            dispatch(
              showToast(
                "Successfully Created Booking Request!",
                "success",
                true,
              ),
            );
            navigate("/booking-request-list");
          })
          .catch(() => {
            dispatch(showToast("Something went wrong", "error"));
          });
      }
    } catch (e) {
      dispatch(showToast("Something went wrong", "error"));
      console.log(e);
    } finally {
      dispatch(callLoader(0));
    }
  };

  const _getPortCode = (strdata) => {
    if (!strdata) return "";
    const match = strdata.match(/\[(.*?)\]/);
    return match ? match[1] : "";
  };

  const AddListofVessel = async () => {
    try {
      dispatch(callLoader(1));
      const payloadVessel = {
        CarrierScac: formData?.carrierSCAC,
        originPortCode: formData?.portOfLoadCode,
        destinationPortCode: formData?.portOfDischargeCode,
        searchDate: formatDate(formData?.effectiveDate),
      };
      const list = await dispatch(GetVesselSchedule(payloadVessel));
      setVesselList(list);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching vessel schedule:", error);
      setVesselList([]);
    } finally {
      dispatch(callLoader(0));
    }
  };

  const handleCheckboxChange = (item, isChecked) => {
    let updated = [...selectedVessels];
    const notDeletedCount = vessel.filter((r) => r?.isDeleted == 0).length;
    if (notDeletedCount >= 3) {
      dispatch(showToast("Max 3 Vessel Charge Can Add!", "error"));
      return;
    }
    if (isChecked) {
      if (updated.length + notDeletedCount >= 3) {
        dispatch(showToast("Max 3 Vessel Charge Can Add!", "error"));
        return;
      }
      if (updated.length >= 3) {
        setError("You can select maximum 3 vessels only");
        return;
      }
      updated.push(item);
    } else {
      updated = updated.filter((v) => v !== item);
    }
    setError("");
    setSelectedVessels(updated);
  };

  const handleSaveVessels = () => {
    const safeSelected = Array.isArray(selectedVessels) ? selectedVessels : [];
    if (safeSelected.length === 0) {
      setError("Please select at least one vessel");
      return;
    }
    const toISO = (date) => (date ? new Date(date).toISOString() : null);
    const formatted = safeSelected.map((v, index) => ({
      vesselName: v.vesselName || "",
      voyage: v.voyageNumber || "",
      cargoCutoff: toISO(v.terminalCutoff),
      eta: toISO(v.destinationArrivalDate),
      etd: toISO(v.originDepartureDate),
      transitTime: v.totalDuration ? String(v.totalDuration) : null,
      freightId: 0,
      id: 0,
      isDeleted: 0,
      lineSeqNo: index + 1,
      carrierId: formData?.carrierSno || 0,
      vesselId: v.vesselId || 0,
    }));
    setVessel((prev) => [...prev, ...formatted]);
    setSelectedVessels([]);
    setShowModal(false);
    setError("");
  };

  const settAllin = (prew) => {
    setCarriers(carriers.map((item) => ({ ...item, all_in: prew })));
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Sticky PageHeader ── */}
      <div className="page-header-sticky sticky top-0 z-50">
        <PageHeader
          icon={<FileText className="w-5 h-5 text-white" />}
          title="Booking Request"
        
          subtitle={
            idQuote ? (
              <>
                {"Quote No. "}
                <span className="text-slate-700 font-semibold dark:text-slate-300">
                  {formData?.bkgRqNumber}
                </span>
              </>
            ) : (
              "Create and manage Booking Requests"
            )
          }
          actions={[
            {
              label: "Cancel",
              icon: <X />,
              onClick: () => navigate("/booking-request-list"),
              variant: "ghost",
            },
            ...(idQuote
              ? [
                  {
                    label: "Print",
                    icon: <Printer />,
                    onClick: () =>
                      window.open(
                        `/booking-request/print/${btoa(idQuote)}`,
                        "_blank",
                      ),
                    variant: "tertiary",
                  },
                  {
                    label: "Email",
                    icon: <Mail />,
                    onClick: () =>
                      navigate(
                        `/mail/compose?type=BookingRequest&id=${idQuote}`,
                      ),
                    variant: "tertiary",
                  },
                  {
                    label: "Convert Booking",
                    icon: <BookOpen />,
                    onClick: () => setShowCreateBookingModal(true),
                    variant: "tertiary",
                  },
                ]
              : []),
            "|",
            {
              label: "AI Tips",
              icon: <Sparkles />,
              onClick: getAITip,
              variant: "tertiary",
            },
          ]}
          onSubmit={handleSubmit}
          submitLabel={"Submit Booking Request"}
        />
        <div style={{ height: "var(--page-header-height)" }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 space-y-3 pb-16"
      >
        {/* ── AI Tips Panel ── */}
        <AnimatePresence>
          {showAITip && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg border border-violet-200 dark:border-violet-800 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 flex-1">
                    <div className="p-1.5 bg-violet-100 dark:bg-violet-800/50 rounded-md mt-0.5">
                      <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-violet-800 dark:text-violet-300 mb-1">
                        AI Tips
                      </h3>
                      {aiTipLoading ? (
                        <div className="flex items-center gap-2 text-xs text-violet-600 dark:text-violet-400">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Analyzing your booking data...
                        </div>
                      ) : (
                        <p className="text-xs text-violet-700 dark:text-violet-300 whitespace-pre-line leading-relaxed">
                          {aiTipContent}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAITip(false)}
                    className="p-1 hover:bg-violet-200 dark:hover:bg-violet-800 rounded-md transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-violet-500" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Progress Bar (identical to RateQuote) ── */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 overflow-x-auto">
          <div className="flex items-center min-w-max gap-0">
            {steps.map((step, index) => {
              const sectionKey = stepSectionMap[index];
              const filled = isSectionFilled(sectionKey);
              const done = filled && index !== currentStep;
              const active = index === currentStep;
              return (
                <div key={step} className="flex items-center">
                  <div
                    className={[
                      "flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none whitespace-nowrap",
                      done &&
                        "text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20",
                      active &&
                        "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
                      !done &&
                        !active &&
                        "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => scrollToSection(index)}
                  >
                    <span
                      className={[
                        "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0",
                        done && "bg-green-500 text-white",
                        active && "bg-blue-600 text-white",
                        !done &&
                          !active &&
                          "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {done ? <Check className="w-3 h-3" /> : index + 1}
                    </span>
                    <span className="hidden sm:inline">{step}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={[
                        "w-4 h-0.5 flex-shrink-0 mx-1 rounded-full transition-colors duration-300",
                        done
                          ? "bg-green-400 dark:bg-green-600"
                          : "bg-slate-200 dark:bg-slate-700",
                      ].join(" ")}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Customer + Carrier Grid ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {/* Customer Information */}
          <div
            ref={sectionRefs.customer}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
          >
            <SectionHeader
              icon={User}
              title="Customer Information"
              color="bg-blue-500"
              section="customer"
            />
            {expandedSections.customer && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                className="p-3 space-y-3"
              >
                <InputField label="Customer" className="relative" required>
                  <button
                    className="absolute top-[-3px] left-[80px] text-slate-400"
                    onClick={() => setShowRateQuoteNote(true)}
                  >
                    <NotepadText className="w-5" />
                  </button>
                  <SearchBar
                    searchAPI={
                      "v1/AutoSuggestion/GetCustomerSuggestion?Prefix="
                    }
                    selectReacord={(d) => {
                      handleInputChange({
                        target: { name: "customerName", value: d?.[1] },
                      });
                      handleInputChange({
                        target: { name: "customerCode", value: d?.[0] },
                      });
                      _getAddressBookNotes(d?.[0]);
                      _setAddress("customerAddress", d?.[0]);
                      _setCotact(d?.[0]);
                    }}
                    value={formData?.customerName}
                  />
                </InputField>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Email">
                    <input
                      type="email"
                      name="customerEmail"
                      value={formData?.customerEmail}
                      onChange={handleInputChange}
                      placeholder="email@company.com"
                      className={inputClass}
                    />
                  </InputField>
                  <InputField label="Contact">
                    <input
                      type="text"
                      name="contactPerson"
                      value={formData?.contactPerson}
                      onChange={handleInputChange}
                      placeholder="Contact Name"
                      className={inputClass}
                    />
                  </InputField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Sales Person" required>
                    <select
                      name="salesPersonSno"
                      value={formData?.salesPersonSno}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select</option>
                      {salesPersonList?.map((v, i) => (
                        <option key={i} value={v?.salesPersonId}>
                          {v?.salesPerson}
                        </option>
                      ))}
                    </select>
                  </InputField>
                  <InputField label="Phone">
                    <input
                      type="text"
                      name="customerTel"
                      value={formData?.customerTel}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                      className={inputClass}
                    />
                  </InputField>
                </div>
                <InputField label="Address" required>
                  <textarea
                    name="customerAddress"
                    value={formData?.customerAddress}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Full address..."
                    className={inputClass}
                  />
                </InputField>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Customer Ref #">
                    <input
                      type="text"
                      name="poNumber"
                      value={formData?.poNumber}
                      onChange={handleInputChange}
                      placeholder="O24966"
                      className={inputClass}
                    />
                  </InputField>
                </div>
              </motion.div>
            )}
          </div>

          {/* Carrier & Quote Details */}
          <div
            ref={sectionRefs.carrier}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
          >
            <SectionHeader
              icon={Ship}
              title="Carrier & Quote Details"
              color="bg-cyan-500"
              section="carrier"
            />
            {expandedSections.carrier && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                className="p-3 space-y-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Quote No.">
                    <div className="text-lg text-[#1D4ED8] font-bold">
                      {formData?.quoteNo ?? "-"}
                    </div>
                  </InputField>
                  <InputField label="Quoted By">
                    <select
                      name="quotedBy"
                      value={formData?.quotedBy?.toUpperCase()}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">Select</option>
                      {fileManagerList?.map((v, i) => (
                        <option key={i} value={v?.filemanager}>
                          {v?.filemanager}
                        </option>
                      ))}
                    </select>
                  </InputField>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Office Type" required>
                    <select
                      name="officeType"
                      value={formData?.officeType}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">--Select--</option>
                      <option value="NEW YORK">NEW YORK</option>
                    </select>
                  </InputField>
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Hide Carrier Name">
                      <Input
                        type="checkbox"
                        name="carrierHide"
                        checked={formData?.carrierHide || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </InputField>
                    <InputField label="Mark NRA RQ">
                      <Input
                        type="checkbox"
                        name="isNRA_RateQuote"
                        checked={formData?.isNRA_RateQuote || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </InputField>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Effective Date" required>
                    <DatePicker
                      selected={
                        formData?.effectiveDate
                          ? moment(formData?.effectiveDate).toDate()
                          : null
                      }
                      onChange={(date) => {
                        handleInputChange({
                          target: { name: "effectiveDate", value: date },
                        });
                        handleInputChange({
                          target: {
                            name: "valideDate",
                            value: moment(date).add(30, "days").toDate(),
                          },
                        });
                      }}
                      className={inputClass}
                      showIcon
                      dateFormat="dd MMM yyyy"
                    />
                  </InputField>
                  <InputField label="Expiry Date" required>
                    <DatePicker
                      selected={
                        formData?.valideDate
                          ? moment(formData?.valideDate).toDate()
                          : null
                      }
                      onChange={(date) =>
                        handleInputChange({
                          target: { name: "valideDate", value: date },
                        })
                      }
                      className={inputClass}
                      showIcon
                      dateFormat="dd MMM yyyy"
                      minDate={
                        formData?.effectiveDate
                          ? moment(formData.effectiveDate)
                              .add(1, "day")
                              .toDate()
                          : null
                      }
                    />
                  </InputField>
                </div>
                <InputField label="Shipping Type" required>
                  <div className="flex items-center gap-6 mt-2">
                    {["FCL", "LCL", "Break Bulk"].map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 cursor-pointer group"
                      >
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${formData?.shippingTerm === type ? "border-blue-500 bg-blue-500" : "border-slate-300 dark:border-slate-600 group-hover:border-blue-400"}`}
                        >
                          {formData?.shippingTerm === type && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <input
                          type="radio"
                          name="shippingTerm"
                          value={type}
                          checked={formData?.shippingTerm === type}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {type}
                        </span>
                      </label>
                    ))}
                  </div>
                </InputField>
                <InputField label="Transport">
                  <select
                    name="modeOfTransport"
                    value={formData?.modeOfTransport}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="OCEAN">OCEAN</option>
                    <option value="AIR">AIR</option>
                    <option value="TRUCK">TRUCK</option>
                  </select>
                </InputField>
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Inco Term">
                    <select
                      className={selectClass}
                      value={formData?.incoterm || ""}
                      onChange={(d) =>
                        handleInputChange({
                          target: { name: "incoterm", value: d.target.value },
                        })
                      }
                    >
                      <option value="">SELECT</option>
                      {incoTermsList?.map((term, key) => (
                        <option key={key} value={term?.incoterm}>
                          {term?.incoterm}
                        </option>
                      ))}
                    </select>
                  </InputField>
                  <InputField label="Shipment Number">
                    <input
                      type="text"
                      name="shippingTerm"
                      value={formData?.shippingTerm}
                      onChange={handleInputChange}
                      placeholder="-NA-"
                      className={inputClass}
                    />
                  </InputField>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* ── Single vs Multi Carrier Toggle ── */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
          <div className="p-4">
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setQuoteType("single")}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${quoteType === "single" ? "bg-blue-600 text-white shadow-lg" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
              >
                <Ship className="w-5 h-5" /> Single Carrier
              </button>
              <button
                onClick={() => setQuoteType("multi")}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${quoteType === "multi" ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
              >
                <Users className="w-5 h-5" /> Multi Carrier
                {multiCarriers.filter((c) => !c.isDeleted).length > 0 && (
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {multiCarriers.filter((c) => !c.isDeleted).length}
                  </span>
                )}
              </button>
            </div>
            {quoteType === "multi" && (
              <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-2">
                Add multiple carriers with their own container, commodity,
                routing, vessel, and charges information
              </p>
            )}
          </div>
        </div>

        {/* ── Multi-Carrier Section ── */}
        {quoteType === "multi" && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
            <div className="flex justify-end mb-4 p-4">
              <Button
                onClick={handleAddCarrier}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Carrier
              </Button>
            </div>
            {formData?.bookingRequestCarrierDetails?.map((carrier, index) => (
              <MultiCarrierSection
                key={index}
                carrierIndex={index}
                CarrierDetails={carrier}
                allCarrierList={formData?.bookingRequestCarrierDetails ?? []}
                handleSaveVessels={handleSaveVessels}
                originPortCode={formData?.portOfLoadCode}
                destinationPortCode={formData?.portOfDischargeCode}
                searchDate={formatDate(formData?.effectiveDate)}
                onChange={(updatedCarrier) => {
                  const updatedCarriers = [
                    ...formData.bookingRequestCarrierDetails,
                  ];
                  updatedCarriers[index] = updatedCarrier;
                  handleInputChange({
                    target: { name: "CarrierDetails", value: updatedCarriers },
                  });
                }}
                onDelete={() => {
                  const updatedCarriers =
                    formData.bookingRequestCarrierDetails.filter(
                      (_, i) => i !== index,
                    );
                  handleInputChange({
                    target: { name: "CarrierDetails", value: updatedCarriers },
                  });
                }}
              />
            ))}
          </div>
        )}

        {/* ── Container & Commodity ── */}
        <div
          ref={sectionRefs.container}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
        >
          <SectionHeader
            icon={Package}
            title="Carrier, Container & Commodity"
            color="bg-orange-500"
            section="container"
          />
          {expandedSections.container && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="p-3"
            >
              <div className="grid grid-cols-2 gap-4 justify-start items-start">
                <div className="grid grid-cols-1 gap-4">
                  <InputField label="Commodity" required>
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetCommoditySuggestion?Prefix="
                      }
                      selectReacord={(d) =>
                        handleInputChange({
                          target: { name: "commodity", value: d?.[1] },
                        })
                      }
                      value={formData?.commodity || ""}
                      onchange={(value) =>
                        handleInputChange({
                          target: { name: "commodity", value },
                        })
                      }
                    />
                  </InputField>
                  <InputField label="Trucker">
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetTruckerAndTowing?Prefix="
                      }
                      selectReacord={(d) => {
                        handleInputChange({
                          target: { name: "truckerName", value: d?.[1] },
                        });
                        handleInputChange({
                          target: { name: "trucker_ID", value: d?.[0] },
                        });
                        _setAddress("truckerAddress", d?.[0]);
                      }}
                      value={formData?.truckerName || ""}
                    />
                  </InputField>
                  <InputField label="Trucker Details:">
                    <Textarea
                      name="truckerAddress"
                      value={formData?.truckerAddress}
                      onChange={handleInputChange}
                      rows="2"
                      className={`mt-2 ${inputClass}`}
                    />
                  </InputField>
                </div>
                <div>
                  {quoteType === "single" && (
                    <div>
                      <div className="grid grid-cols-2 items-center">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Container Details:
                        </span>
                        <Button
                          variant={"outline"}
                          onClick={addContainerChange}
                          className="justify-self-end"
                        >
                          <Plus /> Add Container
                        </Button>
                      </div>
                      <div className="mt-4">
                        {container?.map((cnt, index) => {
                          const deactiveCount =
                            container?.filter((c) => c?.isDeleted == 1)
                              ?.length || 0;
                          return (
                            cnt?.isDeleted !== 1 && (
                              <div
                                key={index}
                                className="grid grid-cols-7 gap-2 mb-2 items-center"
                              >
                                <input
                                  type="number"
                                  min="1"
                                  value={cnt?.numberOfContainer}
                                  onChange={(e) =>
                                    handleContainerChange(
                                      index,
                                      "numberOfContainer",
                                      e.target.value,
                                    )
                                  }
                                  className={`w-20 ${inputClass} col-span-3`}
                                />
                                <select
                                  value={cnt?.contTypeCode}
                                  onChange={(e) => {
                                    handleContainerChange(
                                      index,
                                      "contTypeCode",
                                      e.target.value,
                                    );
                                    handleContainerChange(
                                      index,
                                      "contTypeName",
                                      e.target.options[e.target.selectedIndex]
                                        .text,
                                    );
                                  }}
                                  className={`flex-1 ${selectClass} col-span-3`}
                                >
                                  <option value="">Select</option>
                                  {containerTypeList?.map((c, key) => (
                                    <option key={key} value={c.moveTypeId}>
                                      {c.moveType}
                                    </option>
                                  ))}
                                </select>
                                <div className="grid grid-cols-1 gap-2">
                                  {container?.length - 1 == index && (
                                    <Button
                                      variant={"outline"}
                                      onClick={() =>
                                        deleteContainerChange(
                                          index,
                                          "isDeleted",
                                          1,
                                          index - deactiveCount,
                                        )
                                      }
                                    >
                                      <Trash2 />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                        Hazardous Cargo
                      </span>
                    </div>
                    <div className="flex items-center gap-6 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Input
                          type="radio"
                          name="hazCargo"
                          checked={formData?.hazCargo}
                          onChange={() =>
                            handleInputChange({
                              target: { name: "hazCargo", value: true },
                            })
                          }
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          Yes
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Input
                          type="radio"
                          name="hazCargo"
                          checked={!formData?.hazCargo}
                          onChange={() =>
                            handleInputChange({
                              target: { name: "hazCargo", value: false },
                            })
                          }
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">
                          No
                        </span>
                      </label>
                    </div>
                    {formData?.hazCargo && (
                      <div className="grid grid-cols-3 gap-3">
                        <InputField label="IMO Class">
                          <input
                            type="text"
                            name="imoClass"
                            value={formData?.imoClass}
                            onChange={handleInputChange}
                            className={inputClass}
                          />
                        </InputField>
                        <InputField label="PKG #">
                          <input
                            type="text"
                            name="package"
                            value={formData?.package}
                            onChange={handleInputChange}
                            className={inputClass}
                          />
                        </InputField>
                        <InputField label="UN NBR">
                          <input
                            type="text"
                            name="unnbr"
                            value={formData?.unnbr}
                            onChange={handleInputChange}
                            className={inputClass}
                          />
                        </InputField>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {quoteType === "single" && (
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <InputField label="Carrier" required>
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetCarrierSuggestion?Prefix="
                      }
                      selectReacord={(d) => {
                        handleInputChange({
                          target: { name: "carrierName", value: d?.[1] },
                        });
                        handleInputChange({
                          target: { name: "carrierSno", value: d?.[0] },
                        });
                        handleInputChange({
                          target: { name: "carrierSCAC", value: d?.[2] },
                        });
                        handleInputChange({
                          target: { name: "carrierContract", value: d?.[3] },
                        });
                        handleInputChange({
                          target: {
                            name: "rateQuoteReinstateRemarks",
                            value: d?.[4],
                          },
                        });
                      }}
                      value={formData?.carrierName}
                    />
                  </InputField>
                  <InputField label="Carrier Contact / R/Q #">
                    <input
                      type="text"
                      name="carrierContract"
                      value={formData?.carrierContract}
                      onChange={handleInputChange}
                      className={inputClass}
                    />
                  </InputField>
                  <InputField label="Carrier Notes">
                    <input
                      type="text"
                      name="rateQuoteReinstateRemarks"
                      value={formData?.rateQuoteReinstateRemarks}
                      onChange={handleInputChange}
                      className={inputClass}
                    />
                  </InputField>
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* ── Routing Information ── */}
        <div
          ref={sectionRefs.routing}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
        >
          <SectionHeader
            icon={MapPin}
            title="Routing Information"
            color="bg-purple-500"
            section="routing"
          />
          {expandedSections.routing && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="p-3"
            >
              {/* ── Visual Route Display (identical to RateQuote) ── */}
              <div className="mb-6 px-6 py-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 flex-shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md shadow-blue-400/30">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
                        Origin
                      </p>
                      <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                        <InlineCountryFlag code={formData?.portOfLoadCode} />
                        <span>{formData?.portOfLoad || "Port of Loading"}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 flex items-center relative mx-2 h-8">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                    <div className="flex-1 h-0.5 self-center bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 relative">
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <div className="w-9 h-9 rounded-full bg-white dark:bg-slate-900 border-2 border-slate-200 flex items-center justify-center shadow-md shadow-cyan-300/20">
                          <Ship className="w-4 h-4 text-cyan-500" />
                        </div>
                      </div>
                    </div>
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                  </div>
                  <div className="flex items-center gap-3 min-w-0 flex-row-reverse">
                    <div className="w-11 h-11 flex-shrink-0 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-400/30">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 text-right">
                      <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
                        Destination
                      </p>
                      <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                        <InlineCountryFlag
                          code={formData?.portOfDischargeCode}
                        />
                        <span>
                          {formData?.portOfDischarge || "Port of Discharge"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="space-y-3">
                  <InputField label="Carrier Move Type" required>
                    <select
                      name="moveType"
                      value={formData?.moveType}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="">SELECT TYPE</option>
                      {moveTypeList.map((item, index) => (
                        <option key={index} value={item.moveType}>
                          {item.moveType}
                        </option>
                      ))}
                    </select>
                  </InputField>
                  <InputField label="Place of Receipt">
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                      }
                      selectReacord={(d) => {
                        handleInputChange({
                          target: { name: "origin", value: d?.[1] },
                        });
                        handleInputChange({
                          target: {
                            name: "originCode",
                            value: _getPortCode(d?.[1]),
                          },
                        });
                      }}
                      onchange={(value) => {
                        if (!value) {
                          handleInputChange({
                            target: { name: "origin", value: "" },
                          });
                          handleInputChange({
                            target: { name: "originCode", value: "" },
                          });
                        }
                      }}
                      value={formData?.origin}
                    />
                  </InputField>
                  <InputField label="Port of Loading" required>
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                      }
                      selectReacord={(d) => {
                        handleInputChange({
                          target: { name: "portOfLoad", value: d?.[1] },
                        });
                        handleInputChange({
                          target: {
                            name: "portOfLoadCode",
                            value: _getPortCode(d?.[1]),
                          },
                        });
                      }}
                      onchange={(value) => {
                        if (!value) {
                          handleInputChange({
                            target: { name: "portOfLoad", value: "" },
                          });
                          handleInputChange({
                            target: { name: "portOfLoadCode", value: "" },
                          });
                        }
                      }}
                      value={formData?.portOfLoad}
                    />
                  </InputField>
                </div>
                <div className="space-y-3">
                  <InputField label="Port of Discharge" required>
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                      }
                      selectReacord={(d) => {
                        handleInputChange({
                          target: { name: "portOfDischarge", value: d?.[1] },
                        });
                        handleInputChange({
                          target: {
                            name: "portOfDischargeCode",
                            value: _getPortCode(d?.[1]),
                          },
                        });
                      }}
                      onchange={(value) => {
                        if (!value) {
                          handleInputChange({
                            target: { name: "portOfDischarge", value: "" },
                          });
                          handleInputChange({
                            target: { name: "portOfDischargeCode", value: "" },
                          });
                        }
                      }}
                      value={formData?.portOfDischarge}
                    />
                  </InputField>
                  <InputField label="Place of Delivery">
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                      }
                      selectReacord={(d) => {
                        handleInputChange({
                          target: { name: "destination", value: d?.[1] },
                        });
                        handleInputChange({
                          target: {
                            name: "destinationCode",
                            value: _getPortCode(d?.[1]),
                          },
                        });
                      }}
                      onchange={(value) => {
                        if (!value) {
                          handleInputChange({
                            target: { name: "destination", value: "" },
                          });
                          handleInputChange({
                            target: { name: "destinationCode", value: "" },
                          });
                        }
                      }}
                      value={formData?.destination}
                    />
                  </InputField>
                  <InputField label="Loading Location">
                    <SearchBar
                      searchAPI={
                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                      }
                      selectReacord={(d) =>
                        handleInputChange({
                          target: { name: "loadingLoc", value: d?.[1] },
                        })
                      }
                      onchange={(value) => {
                        if (!value)
                          handleInputChange({
                            target: { name: "loadingLoc", value: "" },
                          });
                      }}
                      value={formData?.loadingLoc}
                    />
                  </InputField>
                  <InputField label="Transit Time">
                    <input
                      type="number"
                      name="transitTime"
                      value={formData?.transitTime}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "transitTime",
                            value: String(e.target.value),
                          },
                        })
                      }
                      className={inputClass}
                    />
                  </InputField>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* ── Vessel Charges ── */}
        {quoteType === "single" && (
          <div
            ref={sectionRefs.charges}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
          >
            <SectionHeader
              icon={DollarSign}
              title="Vessel Charges"
              color="bg-emerald-500"
              section="vessel"
              badge="3 items"
            />
            {expandedSections.vessel && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                className="p-3"
              >
                <div className="flex items-center justify-between mb-3 p-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Ship className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="font-semibold text-sm">List</span>
                  </div>
                  <button
                    type="button"
                    onClick={AddListofVessel}
                    className="px-3 py-1 bg-green-600 text-white rounded text-xs"
                  >
                    GetVessel
                  </button>
                  <button
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                    onClick={addVesselCharge}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Vessel Charge
                  </button>
                </div>
                <div className="overflow-x-auto overflow-y-visible pb-[20vh]">
                  <table className="w-full text-xs ev_ar_table">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          Vessel
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          Voyage
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          C/O
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          ETD
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20">
                          ETA
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20">
                          Transit Time
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {vessel?.map(
                        (v, i) =>
                          v?.isDeleted !== 1 && (
                            <tr
                              key={i}
                              className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
                            >
                              <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                <SearchBar
                                  searchAPI={
                                    "v1/AutoSuggestion/GetVesselList?Prefix="
                                  }
                                  value={v?.vesselName || ""}
                                  onchange={(value) =>
                                    setVessel((prev) => {
                                      const u = [...prev];
                                      u[i] = {
                                        ...u[i],
                                        vesselName: value,
                                        vesselId: 0,
                                      };
                                      return u;
                                    })
                                  }
                                  selectReacord={(d) =>
                                    setVessel((prev) => {
                                      const u = [...prev];
                                      u[i] = {
                                        ...u[i],
                                        vesselName: d?.[1] || "",
                                        vesselId: d?.[0] || 0,
                                      };
                                      return u;
                                    })
                                  }
                                />
                              </td>
                              <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                <Input
                                  type="text"
                                  value={v?.voyage}
                                  onChange={(e) =>
                                    handleVesselChange(
                                      i,
                                      "voyage",
                                      e.target.value,
                                    )
                                  }
                                  className={inputClass}
                                />
                              </td>
                              <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                <DatePicker
                                  selected={v?.cargoCutoff}
                                  onChange={(date) =>
                                    handleVesselChange(i, "cargoCutoff", date)
                                  }
                                  className={inputClass}
                                  showIcon
                                  dateFormat="dd MMM yyyy"
                                />
                              </td>
                              <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                <DatePicker
                                  selected={v?.etd}
                                  onChange={(date) => {
                                    handleVesselChange(i, "etd", date);
                                    handleVesselChange(i, "eta", null);
                                    handleVesselChange(
                                      i,
                                      "transitTime",
                                      countDays(date, v?.eta),
                                    );
                                  }}
                                  className={inputClass}
                                  showIcon
                                  dateFormat="dd MMM yyyy"
                                  minDate={new Date()}
                                />
                              </td>
                              <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                <DatePicker
                                  selected={v?.eta}
                                  onChange={(date) => {
                                    handleVesselChange(i, "eta", date);
                                    handleVesselChange(
                                      i,
                                      "transitTime",
                                      countDays(v?.etd, date),
                                    );
                                    if (i == 0)
                                      handleInputChange({
                                        target: {
                                          name: "transitTime",
                                          value: countDays(v?.etd, date),
                                        },
                                      });
                                  }}
                                  className={inputClass}
                                  showIcon
                                  dateFormat="dd MMM yyyy"
                                  minDate={
                                    v?.etd
                                      ? moment(v?.etd).add(1, "days").toDate()
                                      : new Date()
                                  }
                                />
                              </td>
                              <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                <Input
                                  type="text"
                                  value={v?.transitTime}
                                  onChange={(e) =>
                                    handleVesselChange(
                                      i,
                                      "transitTime",
                                      e.target.value,
                                    )
                                  }
                                  className={inputClass}
                                />
                              </td>
                              <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                <button
                                  type="button"
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                  onClick={() =>
                                    handleVesselChange(i, "isDeleted", 1)
                                  }
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ),
                      )}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* ── Cargo Details ── */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
          <SectionHeader
            icon={Package}
            title="Cargo Details"
            color="bg-emerald-500"
            section="cargo"
            badge={cargoUnits.length + " items"}
          />
          {expandedSections.cargo && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="p-3"
            >
              <div className="flex items-center justify-between mb-3 p-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Ship className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-sm">Cargo Units</span>
                </div>
                <button
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                  onClick={addCargoRow}
                >
                  <Plus className="w-3.5 h-3.5" /> Add Cargo
                </button>
              </div>
              <div className="overflow-x-auto overflow-y-visible pb-[20vh]">
                <table className="min-w-max table-fixed text-xs ev_ar_table_modified">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800">
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                        No
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase w-[90px]">
                        Piece
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                        Package Type
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase w-[100px]">
                        Marks & Numbers
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase bg-[#fae8ff] w-[90px]">
                        Length
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase bg-[#fae8ff] w-[90px]">
                        Width
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase bg-[#fae8ff] w-[90px]">
                        Height
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase bg-[#fae8ff] w-[90px]">
                        Type
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase bg-emerald-50">
                        Weights
                        <div className="grid grid-cols-2 mt-2 w-[180px]">
                          <div>KGS</div>
                          <div>LBS</div>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase bg-[#6ee7b7]">
                        Measurement
                        <div className="grid grid-cols-2 mt-2 w-[180px]">
                          <div>CFT</div>
                          <div>CBM</div>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                        Container
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                        HS Code
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {cargoUnits.map(
                      (row, index) =>
                        !row?.isdeleted && (
                          <tr
                            key={index}
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
                          >
                            <td className="text-center px-2 py-2 text-slate-600 dark:text-slate-400">
                              {index + 1}
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                type="number"
                                value={row?.pices_No}
                                onChange={(e) =>
                                  handleCargoChange(
                                    index,
                                    "pices_No",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="px-2 py-2">
                              <SearchBar
                                searchAPI={
                                  "v1/AutoSuggestion/GetPackageCode?Prefix="
                                }
                                selectReacord={(d) => {
                                  handleCargoChange(
                                    index,
                                    "units_Type",
                                    d?.[1],
                                  );
                                  handleCargoChange(
                                    index,
                                    "units_Type_ID",
                                    d?.[0] || 0,
                                  );
                                }}
                                onchange={(val) => {
                                  if (!val) {
                                    handleInputChange({
                                      target: { name: "units_Type", value: "" },
                                    });
                                    handleInputChange({
                                      target: {
                                        name: "units_Type_ID",
                                        value: 0,
                                      },
                                    });
                                  }
                                }}
                                value={row?.units_Type || ""}
                              />
                            </td>
                            <td className="px-2 py-2 min-w-[250px]">
                              <Input
                                type="text"
                                value={row?.description}
                                onChange={(e) =>
                                  handleCargoChange(
                                    index,
                                    "description",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                type="number"
                                value={row?.dimensions_L}
                                onChange={(e) =>
                                  handleCargoChange(
                                    index,
                                    "dimensions_L",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                type="number"
                                value={row?.dimensions_W}
                                onChange={(e) =>
                                  handleCargoChange(
                                    index,
                                    "dimensions_W",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                type="number"
                                value={row?.dimensions_H}
                                onChange={(e) =>
                                  handleCargoChange(
                                    index,
                                    "dimensions_H",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="px-2 py-2">
                              <select
                                className={selectClass}
                                value={row?.dimensions_Type}
                                onChange={(e) =>
                                  handleCargoChange(
                                    index,
                                    "dimensions_Type",
                                    e.target.value,
                                  )
                                }
                              >
                                <option>Select</option>
                                <option value="CM">CM</option>
                                <option value="FEET">FEET</option>
                                <option value="INCH">INCH</option>
                                <option value="MT">MT</option>
                              </select>
                            </td>
                            <td className="px-2 py-2 w-[180px]">
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  type="number"
                                  value={row?.weights_KG}
                                  onChange={(e) =>
                                    handleCargoChange(
                                      index,
                                      "weights_KG",
                                      e.target.value,
                                    )
                                  }
                                />
                                <Input
                                  type="number"
                                  value={row?.weights_LB}
                                  onChange={(e) =>
                                    handleCargoChange(
                                      index,
                                      "weights_LB",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td className="px-2 py-2 w-[180px]">
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  type="number"
                                  value={row?.measurment_CFT}
                                  readOnly
                                />
                                <Input
                                  type="number"
                                  value={row?.measurment_CBM}
                                  readOnly
                                />
                              </div>
                            </td>
                            <td className="px-2 py-2 min-w-[200px]">
                              <select
                                className={selectClass}
                                value={row?.container_ForUnit}
                                onChange={(e) =>
                                  handleCargoChange(
                                    index,
                                    "container_ForUnit",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select</option>
                                {containerTypeList?.map((c, key) => (
                                  <option key={key} value={c.moveTypeId}>
                                    {c.moveType}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-2">
                              <SearchBar
                                searchAPI={
                                  "v1/AutoSuggestion/GetHtsusCode?Prefix="
                                }
                                selectReacord={(d) =>
                                  handleCargoChange(index, "hsCode", d?.[0])
                                }
                                value={row?.hsCode || ""}
                                showResults={0}
                              />
                            </td>
                            <td className="px-2 py-2">
                              <button
                                type="button"
                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                onClick={() =>
                                  handleCargoChange(index, "isdeleted", true)
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ),
                    )}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </div>

        {/* ── Carrier Charges ── */}
        {quoteType === "single" && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
            <SectionHeader
              icon={DollarSign}
              title="Carrier Charges"
              color="bg-emerald-500"
              section="charges"
              badge={carriers.length + " items"}
            />
            {expandedSections.charges && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                className="p-3"
              >
                <div className="flex items-center justify-between mb-3 p-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Ship className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="font-semibold text-sm">Rate Charges</span>
                  </div>
                  <button
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                    onClick={addChargeRow}
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Charge
                  </button>
                </div>
                <div className="overflow-x-auto overflow-y-visible pb-[20vh]">
                  <table className="min-w-max table-fixed text-xs ev_ar_table_modified">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            onChange={(e) => settAllin(e.target.checked)}
                          />
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          Currency
                        </th>
                        <th className="px-2 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase w-[90px]">
                          ROE
                        </th>
                        <th className="px-2 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase w-[120px]">
                          Basis
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20 w-[150px]">
                          Sell
                          <br />
                          {_getContainerName(0)}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20 w-[150px]">
                          Buy
                          <br />
                          {_getContainerName(0)}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20 w-[150px]">
                          Sell
                          <br />
                          {_getContainerName(1)}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20 w-[150px]">
                          Buy
                          <br />
                          {_getContainerName(1)}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase bg-emerald-50 dark:bg-emerald-900/20 w-[150px]">
                          Sell
                          <br />
                          {_getContainerName(2)}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase bg-amber-50 dark:bg-amber-900/20 w-[150px]">
                          Buy
                          <br />
                          {_getContainerName(2)}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          Vendor
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">
                          Payment Mode
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {carriers.map(
                        (row, index) =>
                          row?.isdeleted == false && (
                            <tr
                              key={index}
                              className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
                            >
                              <td className="px-2 py-2 min-w-[200px]">
                                <SearchBar
                                  searchAPI={
                                    "v1/Common/ChargesDescription_List?flag=0&searchKeyword="
                                  }
                                  selectReacord={(d) => {
                                    handleRateChange(
                                      index,
                                      "chargedescription",
                                      d?.[1],
                                    );
                                    handleRateChange(
                                      index,
                                      "desccode",
                                      d?.[0].toString(),
                                    );
                                  }}
                                  value={row?.chargedescription || ""}
                                />
                              </td>
                              <td className="px-2 py-2 text-center">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  checked={row?.all_in || false}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "all_in",
                                      e.target.checked,
                                    )
                                  }
                                />
                              </td>
                              <td className="px-1 py-2 w-[70px]">
                                <select
                                  className="w-auto px-2 py-1 text-sm border text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                  value={row?.currency || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "currency",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option>Select</option>
                                  {currency_list?.map((c, key) => (
                                    <option key={key} value={c.currencycode}>
                                      {c.currencycode}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  type="number"
                                  value={row?.roe || 1}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "roe",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="px-1 py-2 w-[120px]">
                                <select
                                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 focus:ring-1 focus:ring-[#0072FF]"
                                  value={row?.basis || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "basis",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option>Select</option>
                                  {charges_basis_list?.map((b, key) => (
                                    <option key={key} value={b.chargebasis}>
                                      {b.chargebasis}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2 min-w-[150px]">
                                <Input
                                  type="number"
                                  value={row?.set1sellrate || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "set1sellrate",
                                      e.target.value,
                                    )
                                  }
                                  disabled={!_getContainerName(0)}
                                />
                              </td>
                              <td className="px-2 py-2 min-w-[150px]">
                                <Input
                                  type="number"
                                  value={row?.set1buyrate || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "set1buyrate",
                                      e.target.value,
                                    )
                                  }
                                  disabled={!_getContainerName(0)}
                                />
                              </td>
                              <td className="px-2 py-2 min-w-[150px]">
                                <Input
                                  type="number"
                                  value={row?.set2sellrate || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "set2sellrate",
                                      e.target.value,
                                    )
                                  }
                                  disabled={!_getContainerName(1)}
                                />
                              </td>
                              <td className="px-2 py-2 min-w-[150px]">
                                <Input
                                  type="number"
                                  value={row?.set2buyrate || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "set2buyrate",
                                      e.target.value,
                                    )
                                  }
                                  disabled={!_getContainerName(1)}
                                />
                              </td>
                              <td className="px-2 py-2 min-w-[150px]">
                                <Input
                                  type="number"
                                  value={row?.set3sellrate || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "set3sellrate",
                                      e.target.value,
                                    )
                                  }
                                  disabled={!_getContainerName(2)}
                                />
                              </td>
                              <td className="px-2 py-2 min-w-[150px]">
                                <Input
                                  type="number"
                                  value={row?.set3buyrate || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "set3buyrate",
                                      e.target.value,
                                    )
                                  }
                                  disabled={!_getContainerName(2)}
                                />
                              </td>
                              <td className="px-2 py-2 min-w-[200px]">
                                <SearchBar
                                  searchAPI={
                                    "v1/AutoSuggestion/GetVendorSuggestion?Prefix="
                                  }
                                  selectReacord={(d) => {
                                    handleRateChange(
                                      index,
                                      "vendor_name",
                                      d?.[1],
                                    );
                                    handleRateChange(index, "vendorid", d?.[0]);
                                  }}
                                  value={row?.vendor_name || ""}
                                />
                              </td>
                              <td className="px-2 py-2">
                                <select
                                  className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                  value={row?.paymentmode || ""}
                                  onChange={(e) =>
                                    handleRateChange(
                                      index,
                                      "paymentmode",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">Select</option>
                                  {["P", "C", "N"].map((mode) => (
                                    <option key={mode} value={mode}>
                                      {mode === "P"
                                        ? "Prepaid"
                                        : mode === "C"
                                          ? "Collect"
                                          : "None"}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                  onClick={() =>
                                    handleRateChange(index, "isdeleted", true)
                                  }
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ),
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#49c3d487] dark:bg-[#012641]">
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className="p-4">
                          <span className="text-xs text-slate-600 dark:text-white">
                            Total
                          </span>
                        </th>
                        {[
                          ["set1TotalSell", "emerald"],
                          ["set1TotalBuy", "amber"],
                          ["set2TotalSell", "purple"],
                          ["set2TotalBuy", "emerald"],
                          ["set3TotalSell", "amber"],
                          ["set3TotalBuy", "purple"],
                        ].map(([key, color]) => (
                          <th key={key} className="p-2">
                            <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                              <span
                                className={`text-lg font-bold text-${color}-600 dark:text-${color}-400`}
                              >
                                ${parseFloat(formData?.[key] ?? 0).toFixed(2)}
                              </span>
                            </div>
                          </th>
                        ))}
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                      <tr className="bg-[#89c2d9] dark:bg-[#013a63]">
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className="p-4">
                          <span className="text-xs text-slate-600 dark:text-white">
                            Total Profit ($)
                          </span>
                        </th>
                        {[
                          ["set1Profit", "emerald"],
                          ["set2Profit", "amber"],
                          ["set3Profit", "purple"],
                        ].map(([key, color]) => (
                          <th key={key} colSpan={2} className="p-2">
                            <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                              <span
                                className={`text-lg font-bold text-${color}-600 dark:text-${color}-400`}
                              >
                                ${parseFloat(formData?.[key] ?? 0).toFixed(2)}
                              </span>
                            </div>
                          </th>
                        ))}
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                      <tr className="bg-[#c1dff0] dark:bg-[#88ccf1]">
                        <th></th>
                        <th></th>
                        <th></th>
                        <th></th>
                        <th className="p-4">
                          <span className="text-xs text-slate-600 dark:text-white">
                            Total Margin (%)
                          </span>
                        </th>
                        {[
                          ["set1ProfitMargin", "emerald"],
                          ["set2ProfitMargin", "amber"],
                          ["set3ProfitMargin", "purple"],
                        ].map(([key, color]) => (
                          <th key={key} colSpan={2} className="p-2">
                            <div className="flex items-center gap-2 px-4 py-2 bg-[#edede9] dark:bg-[#012a4a] rounded-lg justify-center">
                              <span
                                className={`text-lg font-bold text-${color}-600 dark:text-${color}-400`}
                              >
                                {parseFloat(formData?.[key] ?? 0).toFixed(2)}%
                              </span>
                            </div>
                          </th>
                        ))}
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* ── Remarks & Notes ── */}
        <div
          ref={remarksRef}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
        >
          <SectionHeader
            icon={FileText}
            title="Remarks & Notes"
            color="bg-slate-500"
            section="remarks"
          />
          {expandedSections.remarks && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="p-3"
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Customer Remarks
                  </h3>
                  <textarea
                    rows="4"
                    placeholder="Enter customer remarks..."
                    className={inputClass}
                    name="customerRemarks"
                    onChange={handleInputChange}
                    value={formData?.customerRemarks || ""}
                  />
                </div>
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
                  <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" /> Internal Remarks
                  </h3>
                  <textarea
                    rows="4"
                    placeholder="Enter internal remarks..."
                    className={inputClass}
                    name="internalRemarks"
                    onChange={handleInputChange}
                    value={formData?.internalRemarks || ""}
                  />
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Country Clause
                  </h3>
                  <textarea
                    rows="4"
                    placeholder="Enter country clause..."
                    className={inputClass}
                    name="countryClause"
                    onChange={handleInputChange}
                    value={formData?.countryClause || ""}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* ── Clause Section (font sizes identical to RateQuote) ── */}
        <div
          ref={commonClauseRef}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden mt-2 hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200"
        >
          <SectionHeader
            icon={FileText}
            title="Clause"
            color="bg-cyan-500"
            section="commonClause"
          />
          {expandedSections.commonClause && (
            <div className="p-4 border-t border-slate-200 dark:border-slate-800">
              {/* Header Row */}
              <div className="grid grid-cols-12 gap-3 mb-4 text-center">
                <div className="col-span-1">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                    APPLY
                  </label>
                </div>
                <div className="col-span-3">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                    CLAUSE TYPE
                  </label>
                </div>
                <div className="col-span-6">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                    CLAUSE DESCRIPTION
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                    STATUS
                  </label>
                </div>
              </div>
              {clauses?.length > 0 ? (
                clauses.map((clause, index) => (
                  <div
                    key={`clause-${clause.ClauseTypeDescriptionID}-${index}`}
                    className="grid grid-cols-12 gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700 items-center"
                  >
                    <div className="col-span-1 flex justify-center">
                      <input
                        type="checkbox"
                        className="h-3 w-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={clause.applied || false}
                        onChange={(e) =>
                          handleClauseChange(
                            clause.ClauseTypeDescriptionID,
                            "applied",
                            e.target.checked,
                          )
                        }
                      />
                    </div>
                    <div className="col-span-3 flex justify-center items-center">
                      <div className="text-xs font-semibold text-black dark:text-white">
                        {clause.ClauseType}
                      </div>
                    </div>
                    <div className="col-span-6 relative group">
                      <div className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed truncate max-w-full cursor-pointer">
                        {clause.ClauseDescription}
                      </div>
                      <div className="absolute left-0 top-full z-20 hidden group-hover:block w-[420px] p-3 mt-2 text-xs text-white bg-slate-900 rounded-md shadow-xl">
                        {clause.ClauseDescription}
                      </div>
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${clause.applied ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"}`}
                      >
                        {clause.applied ? "Applied" : "Not Applied"}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-4 text-center text-xs text-slate-500">
                  Loading clauses...
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Bottom Actions (identical style to RateQuote) ── */}
        <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-red-500">*</span> Required fields must be
              filled
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => navigate("/booking-request-list")}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50"
              >
                <span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                  <X />
                </span>
                <span className="hidden sm:inline">Cancel</span>
              </button>
              {idQuote != 0 && idQuote != null && idQuote != undefined && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `/booking-request/print/${btoa(idQuote)}`,
                        "_blank",
                      )
                    }
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
                  >
                    <span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                      <Printer />
                    </span>
                    <span className="hidden sm:inline">Print</span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/mail/compose?type=BookingRequest&id=${idQuote}`,
                      )
                    }
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
                  >
                    <span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                      <Mail />
                    </span>
                    <span className="hidden sm:inline">Email</span>
                  </button>
                  <div className="w-px h-6 bg-slate-200 mx-1" />
                </>
              )}
              <CelebrationButton
                onClick={() => handleSubmit()}
                label={"Submit Booking Request"}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-green-600 text-white hover:bg-green-700"
                icon={
                  <span className="w-[15px] h-[15px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full">
                    <Send />
                  </span>
                }
                isSuccess={isAnimate}
              />
            </div>
          </div>
        </div>

        {/* ── Address Book Notes Modal ── */}
        {showRateQuoteNote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
              <table className="w-full text-xs ev_ar_table">
                <thead>
                  <tr className="bg-slate-700 dark:bg-slate-500 text-white">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-100 uppercase">
                      Entity Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-100 uppercase">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {rateQuoteNote.map((note, index) => (
                    <tr
                      key={index}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
                    >
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400 border border-slate-700 dark:border-slate-500">
                        {note?.entityname}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400 border border-slate-700 dark:border-slate-500">
                        {note?.notes || "--NA--"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 justify-end flex">
                <button
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-white rounded-lg font-medium transition-all"
                  onClick={() => setShowRateQuoteNote(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <CarrierSummeryModal
          open={showCarrierSummery}
          onClose={() => setShowCarrierSummery(false)}
        />
        <CreateBookingModal
          open={showCreateBookingModal}
          onClose={() => setShowCreateBookingModal(false)}
          formdata={formData}
        />

        {/* ── Vessel Schedule Modal ── */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3">
            <div className="bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-lg w-full sm:w-[90%] md:w-[80%] lg:w-[70%] max-w-4xl shadow-xl">
              <h2 className="text-lg sm:text-xl font-bold mb-4 text-center text-black dark:text-white">
                Vessel List
              </h2>
              <div className="max-h-[300px] overflow-auto border border-gray-300 dark:border-slate-600">
                <table className="w-full table-fixed border-collapse text-center">
                  <thead className="bg-gray-200 dark:bg-slate-700 text-black dark:text-white sticky top-0 z-10">
                    <tr>
                      <th className="border p-2 w-[120px]">Action</th>
                      <th className="border p-2 w-[30%]">Vessel</th>
                      <th className="border p-2 w-[30%]">Voyage</th>
                      <th className="border p-2 w-[30%]">ETD</th>
                      <th className="border p-2 w-[30%]">ETA</th>
                      <th className="border p-2 w-[30%]">Port C/O</th>
                      <th className="border p-2 w-[30%]">TT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vesselList.map((item, index) => (
                      <tr
                        key={index}
                        className="bg-white dark:bg-slate-800 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-slate-700"
                      >
                        <td className="border p-2 w-[120px]">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedVessels.includes(item)}
                            onChange={(e) =>
                              handleCheckboxChange(item, e.target.checked)
                            }
                          />
                        </td>
                        <td className="border p-2 break-words">
                          {item.vesselName}
                        </td>
                        <td className="border p-2 break-words">
                          {item.voyageNumber}
                        </td>
                        <td className="border p-2 break-words">
                          {item.originDepartureDate}
                        </td>
                        <td className="border p-2 break-words">
                          {item.destinationArrivalDate}
                        </td>
                        <td className="border p-2 break-words">
                          {item.terminalCutoff}
                        </td>
                        <td className="border p-2 break-words">
                          {item.totalDuration}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-center gap-2 mt-4">
                <button
                  onClick={handleSaveVessels}
                  className="px-4 py-1 bg-green-600 hover:bg-green-700 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setSelectedVessels([]);
                    setShowModal(false);
                  }}
                  className="px-4 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                >
                  Close
                </button>
              </div>
              {error && (
                <div className="text-red-500 text-sm text-center mb-2">
                  {error}
                </div>
              )}
            </div>
          </div>
        )}

        <AlertModal
          open={alert.open}
          onOpenChange={alert.handleOpenChange}
          title={alert.title}
          description={alert.description}
          icon={alert.icon}
          iconColor={alert.iconColor}
          confirmText={alert.confirmText}
          cancelText={alert.cancelText}
          onConfirm={alert.handleConfirm}
          onCancel={alert.handleCancel}
          confirmButtonColor={alert.confirmButtonColor}
        />
      </motion.div>
    </>
  );
};

export default NewBookingRequest;
